import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import EditableQuotation from './EditableQuotation';

const EditQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Helper function to format date for HTML input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  useEffect(() => {
    const fetchQuotation = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${apiUrl}/api/quotations/${id}`);
        const quotation = response.data.data || response.data;

        // Transform the quotation data to match the form structure
        const transformedData = {
          id: quotation.id, // Store original quotation ID for update
          quotationNo: quotation.quotation_no || quotation.quotationNo || 'QUOT-001',
          quotationDate: formatDateForInput(quotation.quotation_date || quotation.date),
          customerId: quotation.customer_id || quotation.customerId || null,

          // Customer Information
          customerName: quotation.customer?.name || quotation.customerName || '',
          customerTrn: quotation.customer?.trn || quotation.customerTrn || '',
          customerPhone: quotation.customer?.phone || quotation.customerPhone || '',
          customerAddress: quotation.customer?.address || quotation.customerAddress || '',
          customerCity: quotation.customer?.city || quotation.customerCity || '',
          customerCountry: quotation.customer?.country || quotation.customerCountry || 'UAE',

          // Line Items
          items: (quotation.qlines || quotation.items || []).map(line => ({
            description: line.description || '',
            quantity: parseFloat(line.quantity || 0),
            unit: line.unit || 'Unit',
            unit_price: parseFloat(line.unit_price || line.unitPrice || 0),
            discount_amount: parseFloat(line.discount_amount || line.discountAmount || 0),
            vat_percent: parseFloat(line.vat_percent || line.vatPercent || 0),
          })),

          // Notes
          notes: quotation.notes || '',
        };

        setInitialData(transformedData);
      } catch (err) {
        console.error('Error fetching quotation:', err);
        setError('Unable to load quotation. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchQuotation();
    }
  }, [id, apiUrl]);

  const handleEditSubmit = async (quotationData) => {
    try {
      // Prepare update payload
      const updatePayload = {
        customer_id: quotationData.customerId,
        quotation_date: quotationData.quotationDate,
        notes: quotationData.notes,
        items: quotationData.items,
      };

      // Send update request to API
      await axios.put(`${apiUrl}/api/quotations/${id}`, updatePayload);

      // Show success message and navigate back
      alert('Quotation updated successfully!');
      navigate('/quotations');
    } catch (err) {
      console.error('Error updating quotation:', err);
      alert('Error updating quotation. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading quotation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/quotations')}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Back to Quotations
          </button>
        </div>
      </div>
    );
  }

  return (
    <EditableQuotation 
      initialData={initialData}
      onSubmit={handleEditSubmit}
    />
  );
};

export default EditQuotation;
